import { identifyFullStoryUser, initializeFullStory } from '@modules/fullstory';
import { identifySentryUser, setSentryTags } from '@modules/sentry';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useGetFeatureFlagManager } from 'v2/core/clients/featureFlag';
import { User } from 'v2/core/entities/user';
import { setDataInAnalytics } from 'v2/utils/setDataInAnalytics';

const useGetUser = (): User => {
  const user = useGetUserMaybe();
  if (!user) {
    throw Error(`useGetUser called but the user was not defined. `);
  }

  return useMemo(
    () => ({
      ...user,
      pinnedDashboards:
        user.pinnedDashboards?.filter(
          (dashboard) => dashboard.tenantID === user.tenant.id,
        ) ?? [],
    }),
    [user],
  );
};

const useGetUserMaybe = (): User | undefined => {
  const context = useContext(UserContext);
  if (!context) {
    throw Error(
      `useGetUser called but the context was not defined. Please provide the context before calling`,
    );
  }
  //TODO: store the mapped user in context
  return context.user;
};

const useGetUserSetter = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw Error(
      `useGetUser called but the context was not defined. Please provide the context before calling`,
    );
  }
  return context.setUser;
};

export interface IUserContextProvider {
  user?: User;
  setUser: (user: User) => void;
}

export const UserContext = createContext<IUserContextProvider | undefined>(
  undefined,
);
UserContext.displayName = 'UserContext';

const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | undefined>();
  const { setUser: ffSetUser } = useGetFeatureFlagManager();

  const appSetUser = useCallback(
    (user: User) => {
      setUser(user);
      ffSetUser(user);
      setDataInAnalytics(user);
    },
    [ffSetUser],
  );

  useEffect(() => {
    if (user) {
      initializeFullStory(user.id);
      identifyFullStoryUser(user);
      identifySentryUser(user);
      setSentryTags(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: appSetUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { useGetUser, useGetUserMaybe, useGetUserSetter };
export default UserContextProvider;
